import { Flex } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { Toast } from './toast';
import { ToastProps } from './toast.types';

type NotificationType = ToastProps;

export type NotificationContainerProps = {
  timeout?: number;
};

type Notification = {
  key: number;
  props: NotificationType;
  render: RenderNotificationFn<NotificationType>;
};
type NotificationActions = {
  clear: () => void;
};

type RenderNotificationFn<InnerFunctionProps> = (
  renderFnProps: InnerFunctionProps,
  actions: NotificationActions
) => React.ReactNode;

type AddNotificationFn<InnerFunctionProps> = (
  notificationId: number,
  props: InnerFunctionProps,
  renderFn: RenderNotificationFn<InnerFunctionProps>
) => void;

let addMessage: AddNotificationFn<NotificationType>;
let removeMessage: (notificationId: number) => void;

/**
 * @deprecated This component and its related functions are deprecated.
 * Please use the new toast system provided by the `useToast` hook from '@payflip/ui/v2'.
 *
 * Example usage:
 * import { useToast } from '@payflip/ui/v2';
 *
 * const { showToast } = useToast();
 * showToast({
 *   type: 'success',
 *   header: 'Account created',
 *   body: 'We've created your account for you.',
 *   closable: true,
 * });
 */

export function NotificationContainer({
  timeout = 3000
}: NotificationContainerProps): JSX.Element | null {
  const [items, setItems] = useState<Notification[]>([]);

  useEffect(() => {
    addMessage = (notificationId, props, render): void => {
      setItems((items) => [...items, { key: notificationId, props, render }]);
    };

    removeMessage = (notificationId): void => {
      if (notificationId !== null && notificationId !== undefined) {
        setItems((items) =>
          items.filter((item) => item.key !== notificationId)
        );
      } else {
        setItems([]);
      }
    };
  }, []);

  const transitions = useTransition(items, {
    key: ({ key }: { key: string }) => key,
    from: () => {
      return {
        opacity: 0,
        right: -300
      };
    },
    enter:
      (item) =>
      async (next): Promise<void> => {
        await next({ opacity: 1, right: 0 });
        if (timeout) {
          await next({ delay: timeout });
          setItems((items) => items.filter((i) => i.key !== item.key));
        }
      },
    leave: [{ transform: 'translateY(100px)', opacity: 0 }, { width: 0 }],
    config: { tension: 225, friction: 20, precision: 0.1 }
  });

  if (!items.length) {
    return null;
  }

  return (
    <Flex
      flexDir="column"
      h="100%"
      zIndex="200"
      position="fixed"
      top={0}
      right={0}
    >
      {transitions((style, item) => {
        const { render, props, key } = item;
        return (
          <animated.div style={style}>
            {render(props, { clear: () => clearNotification(key) })}
          </animated.div>
        );
      })}
    </Flex>
  );
}

let id = 0;

export const notificationBuilder = <
  InnerFunctionProps extends NotificationType
>(
  renderFn: RenderNotificationFn<NotificationType>
): ((props: InnerFunctionProps) => {
  notificationId: number;
  actions: NotificationActions;
}) => {
  return (
    props
  ): { notificationId: number; actions: { clear: () => void } } => {
    const notificationId = ++id;

    addMessage(notificationId, props, renderFn);

    return {
      notificationId,
      actions: {
        clear: (): void => {
          clearNotification(notificationId);
        }
      }
    };
  };
};

const clearNotification = (notificationId: number): void => {
  if (removeMessage) {
    removeMessage(notificationId);
  }
};

/**
 * @deprecated This component and its related functions are deprecated.
 * Please use the new toast system provided by the `useToast` hook from '@payflip/ui/v2'.
 **/
const add = notificationBuilder<NotificationType>(
  (notificationsProps: NotificationType, { clear }) => {
    return <Toast {...notificationsProps} onClose={clear} />;
  }
);

/**
 * @deprecated This component and its related functions are deprecated.
 * Please use the new toast system provided by the `useToast` hook from '@payflip/ui/v2'.
 **/
export const toast = {
  add,
  clear: clearNotification
};
